import { Component, Prop, Mixins } from 'vue-property-decorator';

import {
	Backdrop,
	ObjectType,
	TitleListType,
	Maybe,
	TitleListEntry,
	Movie,
	Show,
	Season,
} from '@/@types/graphql-types';
import { TitleListName } from '@/interfaces/responses/title-list';
import { MarkAsSeenModalOptions, MarkAsSeenModalProps } from '@/components/mark-as-seen/MarkAsSeen';
import { ModalHelper } from '@/helpers/modal-helper';
import type { TitleContentType } from '@/interfaces/snowplow/title-context';

import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';
import TitleLikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleLikelistMutationMixin.vue';
import TitleDislikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleDislikelistMutationMixin.vue';

const MarkAsSeenModalGraphql = () => import('@/components/mark-as-seen/MarkAsSeenModalContainerGraphql.vue');

type TitleListEntries = {
	watchlistEntryV2?: Maybe<{ __typename?: 'TitleListEntry' } & Pick<TitleListEntry, 'createdAt'>>;
	likelistEntry?: Maybe<{ __typename?: 'TitleListEntry' } & Pick<TitleListEntry, 'createdAt'>>;
	dislikelistEntry?: Maybe<{ __typename?: 'TitleListEntry' } & Pick<TitleListEntry, 'createdAt'>>;
};

export type ListActionableTitle =
	| ({ __typename: 'Movie' } & Pick<Movie, 'id' | 'objectType' | 'objectId'> & TitleListEntries)
	| ({ __typename: 'Season' } & Pick<Season, 'id' | 'objectType' | 'objectId'> & {
				show: { __typename?: 'Show' } & Pick<Show, 'objectId' | 'objectType'> & TitleListEntries;
			})
	| ({ __typename: 'Show' } & Pick<Show, 'id' | 'objectType' | 'objectId'> & TitleListEntries);

export type TitleQuickActionPayload = {
	type: Exclude<TitleListName, TitleListName.MORELIST>;
	isTitleInList: boolean;
	titleObjectId: number;
	objectType: ObjectType;
};

export type ListMutationTitleDetailParam = {
	objectType: ObjectType;
	objectId: number;
	title: string;
	seasonTitle?: string;
	seasonNumber?: number;
	episodeNumber?: number;
	contentType?: TitleContentType;
};

export type ListMutationShowDetailParam = {
	showObjectId?: number;
};

@Component({ name: 'TitleActionsProvider' })
export default class TitleActionsProvider extends Mixins(
	TitleWatchlistMutationMixin,
	TitleLikelistMutationMixin,
	TitleDislikelistMutationMixin
) {
	@Prop({ required: true }) name: string;
	@Prop({ required: true }) source: string;
	@Prop({ required: true }) title: ListActionableTitle;

	@Prop({ default: undefined }) contentType?: TitleContentType;
	@Prop({ default: undefined }) posterUrl?: string;
	@Prop({ default: undefined }) isTitleReleased?: boolean;
	@Prop({ default: () => [] }) backdrops: Pick<Backdrop, 'backdropUrl'>[];

	private get isInWatchList() {
		return isInWatchList(this.title);
	}

	private get isInLikeList() {
		return isInLikeList(this.title);
	}

	private get isInDislikeList() {
		return isInDislikeList(this.title);
	}

	private get mutationDetails(): ListMutationTitleDetailParam {
		return {
			title: this.name,
			objectId: this.title.objectId,
			objectType: this.title.objectType,
		};
	}

	private async toggleWatchlist() {
		const updatedTitle = await this.mixin_setInWatchlist(this.title.id, this.isInWatchList, this.mutationDetails, {
			source: this.source,
		});

		if (updatedTitle == null) return;

		const isInWatchlist = !!updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt;

		if (
			isInWatchlist &&
			this.isTitleReleased &&
			(this.title.objectType === ObjectType.Show || this.title.objectType === ObjectType.ShowSeason)
		) {
			ModalHelper.openModal(
				MarkAsSeenModalGraphql,
				{
					titleId: this.title.id,
					titleName: this.name,
					titleObjectId: this.title.objectId,
					titleObjectType: this.title.objectType,
					titleContentType: this.contentType,
					posterUrl: this.posterUrl,
					backdrops: this.backdrops,
				} as MarkAsSeenModalProps,
				MarkAsSeenModalOptions,
				'',
				false,
				this.additionalContexts
			);
		}
	}

	private toggleLikelist() {
		this.mixin_setInLikelist(this.title.id, this.isInLikeList, this.isInDislikeList, this.mutationDetails, {
			source: this.source,
		});
	}

	private toggleDislikelist() {
		this.mixin_setInDislikelist(this.title.id, this.isInDislikeList, this.isInLikeList, this.mutationDetails, {
			source: this.source,
		});
	}

	// EXPOSED
	toggle(list: TitleListType) {
		return {
			[TitleListType.WatchNext]: this.toggleWatchlist,
			[TitleListType.Likelist]: this.toggleLikelist,
			[TitleListType.Dislikelist]: this.toggleDislikelist,
			[TitleListType.CaughtUp]: null,
		}[list]?.();
	}

	isIn(list: TitleListType) {
		return {
			[TitleListType.WatchNext]: this.isInWatchList,
			[TitleListType.Likelist]: this.isInLikeList,
			[TitleListType.Dislikelist]: this.isInDislikeList,
			[TitleListType.CaughtUp]: false,
		}[list];
	}

	render() {
		return this.$scopedSlots?.default?.({
			List: TitleListType,
			toggle: this.toggle,
			isIn: this.isIn,
		});
	}
}

export function isInWatchList(title?: ListActionableTitle) {
	if (title == null) return false;

	if (title.__typename === 'Season') {
		return title.show.watchlistEntryV2?.createdAt != null;
	}

	return title.watchlistEntryV2?.createdAt != null;
}

export function isInLikeList(title?: ListActionableTitle) {
	if (title == null) return false;

	if (title.__typename === 'Season') {
		return title.show.likelistEntry?.createdAt != null;
	}

	return title.likelistEntry?.createdAt != null;
}

export function isInDislikeList(title?: ListActionableTitle) {
	if (title == null) return false;

	if (title.__typename === 'Season') {
		return title.show.dislikelistEntry?.createdAt != null;
	}

	return title.dislikelistEntry?.createdAt != null;
}
